/**
 * Insert a canned reply into the ticket composer.
 *
 * WHY A DROPDOWN AND NOT A MODAL
 * The technician is mid-reply. A modal takes over the screen and loses their
 * place; a dropdown anchored to the composer keeps the ticket, the thread and
 * whatever they had already typed all visible while they choose.
 *
 * WHY IT APPENDS RATHER THAN REPLACES
 * Silently discarding text somebody has already written is the kind of thing
 * you only forgive once. If the box is empty the template simply fills it; if
 * not, it is added below what is there.
 *
 * The bodies arrive already rendered for this ticket — the server does the
 * substitution, because it has the customer, contact and device to hand and
 * because two implementations of "what if there is no contact name" would
 * disagree exactly when it shows: in front of a customer.
 */
import { useEffect, useRef, useState } from "react";
import api from "../api/client";

export type ReplyTemplate = {
  id: number;
  name: string;
  category: string | null;
  body: string;
  default_internal: boolean;
  use_count: number;
};

type Props = {
  ticketId: number | string;
  /** Receives the rendered body and whether the template is an internal note. */
  onInsert: (body: string, defaultInternal: boolean) => void;
};

export default function TemplatePicker({ ticketId, onInsert }: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ReplyTemplate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState<ReplyTemplate | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  // Fetched on first open, not on mount: most ticket views never touch this,
  // and rendering every template server-side has a cost per ticket page.
  useEffect(() => {
    if (!open || loaded) return;
    api
      .get(`/api/v1/ticket-templates`, { params: { ticket_id: ticketId } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [open, loaded, ticketId]);

  // Close on an outside click or Escape — a dropdown that traps you is worse
  // than the modal it was meant to avoid.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (t: ReplyTemplate) => {
    onInsert(t.body, t.default_internal);
    setOpen(false);
    setPreview(null);
    // Fire and forget: the count is for ordering the list, and a failed
    // increment must never block the reply the technician came here to send.
    api.post(`/api/v1/ticket-templates/${t.id}/used`).catch(() => {});
  };

  // Grouped so a long list stays navigable. Uncategorised sorts last, because
  // the categories are what someone is scanning for.
  const groups: Record<string, ReplyTemplate[]> = {};
  items.forEach((t) => {
    const k = t.category || "Other";
    (groups[k] = groups[k] || []).push(t);
  });
  const names = Object.keys(groups).sort((a, b) =>
    a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b),
  );

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
        title="Insert a canned reply"
      >
        ⚡ Canned reply
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-[26rem] max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
          {!loaded && (
            <div className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">Loading…</div>
          )}
          {loaded && items.length === 0 && (
            <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
              No canned replies yet. Add them in HelpDesk → Settings.
            </div>
          )}
          {names.map((cat) => (
            <div key={cat}>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
                {cat}
              </div>
              {groups[cat].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => choose(t)}
                  onMouseEnter={() => setPreview(t)}
                  onFocus={() => setPreview(t)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 focus:bg-gray-50 dark:focus:bg-gray-700/40 focus:outline-none"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                      {t.name}
                    </span>
                    {t.default_internal && (
                      <span className="text-[10px] rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 font-medium shrink-0">
                        Internal
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ))}

          {/* The preview shows the text ALREADY FILLED IN for this ticket, so
              what you see is exactly what gets inserted — a template list
              showing raw {{tokens}} makes you insert it to find out. */}
          {preview && (
            <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold mb-1">
                Preview
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {preview.body}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
